import { useMemo, useState } from "react";
import { t } from "@/i18n-js/instance";
import { Loader } from "@/react/components/shared/uikit/Loader";
import { Typography } from "@/react/components/shared/uikit/Typography";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { Template } from "../../../../generated/WorkflowsApi.types";
import { CreateWorkflowItem } from "../../../NewWorkflowModal/CreateWorkflowItem";
import { WorkflowTemplateCard } from "../../../NewWorkflowModal/WorkflowTemplateCard";
import { useCreateWorkflowData } from "../../provider/WorkflowFormDataProvider";
import { TemplateFilter, type TemplateType } from "./TemplateFilter";

const i18nRoot = "settings.workflows.templates";

export const FilteredTemplateList = ({
  shouldIncludeCreateButton,
  onClose,
  shouldAutoAdjust,
}: {
  shouldIncludeCreateButton?: boolean;
  onClose?: () => void;
  shouldAutoAdjust?: boolean;
}) => {
  const {
    templates,
    isLoadingTemplates,
    selectedCategory,
  }: {
    templates: Template[];
    isLoadingTemplates: boolean;
    selectedCategory: string;
  } = useCreateWorkflowData();
  const [selectedTemplateType, setSelectedTemplateType] =
    useState<TemplateType>("all");

  const filteredTemplates = useMemo(
    () =>
      templates.filter(
        template =>
          selectedTemplateType === "all" ||
          template.template_type === selectedTemplateType,
      ),
    [templates, selectedTemplateType],
  );

  return (
    <div className="flex flex-col gap-4">
      {!shouldIncludeCreateButton && (
        <Typography.TitleMd>
          {t([i18nRoot, "title", selectedCategory])}
        </Typography.TitleMd>
      )}
      <TemplateFilter
        selectedTemplateType={selectedTemplateType}
        setSelectedTemplateType={setSelectedTemplateType}
      />
      <Loader show={isLoadingTemplates} />
      {!isLoadingTemplates && (
        <Typography.LabelXs weight="medium">
          {t([i18nRoot, "template_count"], {
            count: filteredTemplates.length,
          })}
        </Typography.LabelXs>
      )}
      <div
        className={classNames({
          "grid gap-6 sm:grid-cols-2 lg:grid-cols-3": !shouldAutoAdjust,
          "flex flex-wrap gap-4": shouldAutoAdjust,
        })}
      >
        {shouldIncludeCreateButton && <CreateWorkflowItem onClose={onClose} />}
        {filteredTemplates.map((template: Template) => (
          <WorkflowTemplateCard
            {...template}
            key={template.id}
            isNew={template.is_new}
            templateKey={template.key}
            templateType={template.template_type}
            onClose={onClose}
          />
        ))}
      </div>
    </div>
  );
};
